<script>
    import { onMount } from "svelte";
    import { toast } from "@zerodevx/svelte-toast";
    import { functions } from "@app/configs/firebase.js";
    import {
        assistantState,
        refreshAssistantStatus,
        updateAgentOverageCap,
    } from "@app/store/assistantStore.svelte.js";
    import { openExternal, BILLING_PORTAL_URL } from "@app/lib/upgradePrompt.js";
    import * as m from "@app/paraglide/messages.js";

    /* The AI Agent comes with Pro and Enterprise (monthly credits); extra credits are billed with the plan. */
    let agent = $derived(assistantState.status?.agent);
    let capInput = $state("");
    let savingCap = $state(false);

    onMount(() => {
        // The assistant panel normally loads this; the modal may open first.
        if (functions && !assistantState.status && !assistantState.statusLoading) {
            refreshAssistantStatus();
        }
    });

    $effect(() => {
        if (Number.isFinite(agent?.capUsd)) capInput = String(agent.capUsd);
    });

    function formatDate(ms) {
        return Number.isFinite(ms) ? new Date(ms).toLocaleDateString() : "";
    }

    async function saveCap() {
        const value = Number(capInput);
        if (!Number.isInteger(value) || value < 0 || value > 1000) return;
        savingCap = true;
        try {
            await updateAgentOverageCap(value);
            toast.push(m.agent_plugin_cap_saved(), { classes: ["toast-success"] });
        } catch (error) {
            console.error("Saving the extra credit limit failed", error);
            toast.push(m.agent_plugin_cap_failed({ message: error.message }), { classes: ["toast-danger"] });
        } finally {
            savingCap = false;
        }
    }
</script>

{#if functions && agent}
    <div class="card mt-3">
        <div class="card-body">
            <div class="d-flex align-items-center gap-2 mb-2">
                <i class="bi bi-magic" aria-hidden="true"></i>
                <h6 class="mb-0">{m.agent_plugin_section_title()}</h6>
                {#if agent.allowed}
                    <span class="badge bg-success">{m.agent_included_badge()}</span>
                {/if}
            </div>

            {#if agent.allowed}
                <p class="small mb-1">
                    {m.agent_plugin_credits({
                        used: Math.floor(agent.creditsUsed ?? 0),
                        limit: agent.creditsLimit ?? agent.includedCredits,
                        date: formatDate(agent.periodEnd),
                    })}
                </p>
                {#if agent.overageUsed > 0}
                    <p class="small mb-1">
                        {m.agent_plugin_overage({
                            credits: Math.floor(agent.overageUsed),
                            cost: (agent.overageCostUsd ?? 0).toFixed(2),
                        })}
                    </p>
                {/if}

                {#if agent.canManage && !agent.trial}
                    <div class="mt-3">
                        <label class="form-label small fw-semibold" for="agent-overage-cap">{m.agent_plugin_cap_label()}</label>
                        <div class="input-group input-group-sm" style="max-width: 16rem;">
                            <span class="input-group-text">$</span>
                            <input
                                id="agent-overage-cap"
                                class="form-control"
                                type="number"
                                min="0"
                                max="1000"
                                step="1"
                                bind:value={capInput}
                            />
                            <button class="btn btn-outline-primary" type="button" disabled={savingCap} onclick={saveCap}>
                                {m.agent_plugin_cap_save()}
                            </button>
                        </div>
                        <div class="form-text">{m.agent_plugin_cap_help()}</div>
                    </div>
                {:else if !agent.canManage}
                    <p class="small text-muted mt-2 mb-0">{m.agent_plugin_owner_only()}</p>
                {/if}
            {:else}
                <p class="small mb-2">{m.agent_upgrade_text()}</p>
                <button class="btn btn-primary btn-sm" type="button" onclick={() => openExternal(BILLING_PORTAL_URL)}>
                    <i class="bi bi-arrow-up-circle me-1" aria-hidden="true"></i>{m.agent_upgrade_button()}
                </button>
            {/if}
        </div>
    </div>
{/if}
